/* eslint-disable max-lines */
import type Konva from "konva";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Group, Image, Transformer } from "react-konva";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackResizeStarted } from "@design-lab/shared/lib/analytics";
import type { PlacementFormPosition } from "@design-lab/shared/types";

import { RemoveIcon, BOX_SIZE } from "./RemoveIcon";
import { TextLabel } from "./SizeLabel";

export type ArtworkDimensions = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const LABEL_OFFSET = 8;

const INFO_HIGHLIGHT_Y_OFFSET = 30;

export type ArtworkGroupProps = {
  artworkId: string;
  placement: PlacementFormPosition;
  artworkImage?: HTMLImageElement | undefined;
  artworkDimensions?: ArtworkDimensions | undefined;
  readonly: boolean;
  draggable: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onResize: (widthInches: number, heightInches: number) => void;
  onDragStart?: () => void;
  onDragEnd?: (params: { x: number; y: number; width: number; height: number }) => void;
  onDragMove?: (params: { x: number; y: number; width: number; height: number }) => void;
  printableArea?: { left: number; top: number; width: number; height: number } | undefined;
  ppi: number;
  scale: number;
  zoom: number;
  roundedWidth: number;
  roundedHeight: number;
  showWidthLabel: boolean;
  showHeightLabel: boolean;
  showRemoveButton?: boolean;
  sizeLabelTagFill: string;
  sizeLabelTextFill: string;
  onRemove: (() => void) | undefined;
  isDark: boolean;
};

export function ArtworkGroup({
  artworkId,
  placement,
  artworkImage,
  artworkDimensions,
  readonly,
  draggable,
  isSelected,
  onSelect,
  onResize,
  onDragStart,
  onDragEnd,
  onDragMove,
  printableArea,
  ppi,
  scale,
  zoom,
  roundedWidth,
  roundedHeight,
  showWidthLabel,
  showHeightLabel,
  sizeLabelTagFill,
  sizeLabelTextFill,
  onRemove,
  isDark,
  showRemoveButton = true
}: ArtworkGroupProps) {
  const { isDesktop } = useResponsive();
  const imageRef = useRef<Konva.Image>(null);
  const groupRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const widthLabelRef = useRef<Konva.Label>(null);
  const heightLabelRef = useRef<Konva.Label>(null);
  const infoHighlighterRef = useRef<Konva.Label>(null);
  const infoHighlighterDesignTabRef = useRef<Konva.Label>(null);

  const boundBoxFunc = useCallback(
    (oldBox: any, newBox: any) => {
      if (!artworkImage || !printableArea) return oldBox;

      const aspectRatio = artworkImage.height / artworkImage.width;
      const minSize = 20;

      const stageScale = imageRef.current?.getStage()?.scaleX() ?? 1;

      const maxWidth = printableArea.width * stageScale;
      const maxHeight = printableArea.height * stageScale;
      const maxAllowedWidth = Math.min(maxWidth, maxHeight / aspectRatio);

      const constrainedWidth = Math.max(minSize, Math.min(newBox.width, maxAllowedWidth));
      const constrainedHeight = constrainedWidth * aspectRatio;

      const x = printableArea.left * stageScale + (maxWidth - constrainedWidth) / 2;
      const y = printableArea.top * stageScale;

      return {
        ...newBox,
        width: constrainedWidth,
        height: constrainedHeight,
        x,
        y
      };
    },
    [artworkImage, printableArea]
  );

  const handleTransform = useCallback(() => {
    if (!imageRef.current || !printableArea) return;

    const node = imageRef.current;

    const scaledWidth = node.width() * node.scaleX();
    const scaledHeight = node.height() * node.scaleY();

    const widthInches = scaledWidth / scale / ppi;
    const heightInches = scaledHeight / scale / ppi;

    const roundedWidthInches = Math.round(widthInches * 100) / 100;
    const roundedHeightInches = Math.round(heightInches * 100) / 100;

    const newWidth = roundedWidthInches * ppi * scale;
    const newHeight = roundedHeightInches * ppi * scale;

    node.width(newWidth);
    node.height(newHeight);
    node.scaleX(1);
    node.scaleY(1);

    node.x(printableArea.left + (printableArea.width - newWidth) / 2);
    node.y(printableArea.top);

    onResize(roundedWidthInches, roundedHeightInches);
  }, [onResize, printableArea, ppi, scale]);

  const handleTransformStart = useCallback(() => {
    if (!imageRef.current || !printableArea) return;

    const side = placement === "back" ? "back" : "front";

    trackResizeStarted({
      placement: side,
      artworkId,
      width: imageRef.current.width(),
      height: imageRef.current.height()
    });
  }, [artworkId, placement, printableArea]);

  const handleSelectEvent = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (readonly || !imageRef.current) return;
      e.cancelBubble = true;
      onSelect();
    },
    [readonly, onSelect]
  );

  const handleDragStart = useCallback(() => {
    if (!draggable) return;

    onDragStart?.();
  }, [draggable, onDragStart]);

  const handleDragMove = useCallback(() => {
    if (!draggable || !imageRef.current) return;

    const node = imageRef.current;
    onDragMove?.({ x: node.x(), y: node.y(), width: node.width(), height: node.height() });
  }, [draggable, onDragMove]);

  const handleDragEnd = useCallback(() => {
    if (!draggable || !imageRef.current) return;

    const node = imageRef.current;
    onDragEnd?.({ x: node.x(), y: node.y(), width: node.width(), height: node.height() });
    handleTransform();
  }, [draggable, onDragEnd, handleTransform]);

  // Manage transformer nodes based on selection state
  useEffect(() => {
    if (!transformerRef.current) return;

    if (isSelected && !readonly && imageRef.current) {
      transformerRef.current.nodes([imageRef.current]);
    } else {
      transformerRef.current.nodes([]);
    }
    transformerRef.current.getLayer()?.batchDraw();
  }, [isSelected, readonly, artworkImage, artworkDimensions]);

  // Adjust label offsets when selected or zoom changes
  useLayoutEffect(() => {
    if (!isSelected || readonly) return;

    const stageScale = widthLabelRef.current?.getStage()?.scaleX() ?? 1;

    if (widthLabelRef.current) {
      const rect = widthLabelRef.current.getClientRect();
      widthLabelRef.current.offsetX(rect.width / 2 / stageScale);
    }
    if (heightLabelRef.current) {
      const rect = heightLabelRef.current.getClientRect();
      heightLabelRef.current.offsetX(rect.width / stageScale);
      heightLabelRef.current.offsetY(rect.height / 2 / stageScale);
    }
  }, [isSelected, artworkDimensions, readonly, zoom]);

  // Center non-design tab info highlighter horizontally (zoom-aware)
  useLayoutEffect(() => {
    [infoHighlighterDesignTabRef.current, infoHighlighterRef.current].forEach(label => {
      if (label) {
        const stageScale = label.getStage()?.scaleX() ?? 1;
        const rect = label.getClientRect();
        label.offsetX(rect.width / 2 / stageScale);
      }
    });
  }, [artworkDimensions, zoom, isSelected]);

  const labelsOffset = LABEL_OFFSET / zoom;
  const infoHighlighterYOffset = INFO_HIGHLIGHT_Y_OFFSET / zoom;

  return (
    <Group ref={groupRef}>
      {artworkImage && artworkDimensions && (
        <Image
          ref={imageRef}
          image={artworkImage}
          x={artworkDimensions.x}
          y={artworkDimensions.y}
          width={artworkDimensions.width}
          height={artworkDimensions.height}
          draggable={draggable}
          onClick={handleSelectEvent}
          onTap={handleSelectEvent}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onTransform={handleTransform}
          onTransformStart={handleTransformStart}
        />
      )}
      <Transformer
        ref={transformerRef}
        visible={true}
        rotateEnabled={false}
        flipEnabled={false}
        keepRatio={true}
        enabledAnchors={["bottom-left", "bottom-right"]}
        boundBoxFunc={boundBoxFunc}
        borderStroke="#4864F9"
        borderDash={[4, 2]}
        borderStrokeWidth={2}
        anchorFill="white"
        anchorStroke="#4864F9"
        anchorSize={12}
        anchorCornerRadius={0}
        centeredScaling={false}
        ignoreStroke={true}
      />

      {/* Labels and controls - always show in normal mode */}
      {!readonly && artworkImage && artworkDimensions && !isSelected && (
        <TextLabel
          ref={infoHighlighterRef}
          x={artworkDimensions.x + artworkDimensions.width / 2}
          y={artworkDimensions.y + artworkDimensions.height + infoHighlighterYOffset}
          tagFill={sizeLabelTagFill}
          textFill={sizeLabelTextFill}
          align={isDesktop ? "left" : "center"}
          fontSize={14}
          stroke={isDark ? "#fb64b6" : "#FFFFFF"}
          strokeWidth={2}
        >
          Click design to resize
        </TextLabel>
      )}
      {artworkImage && artworkDimensions && showWidthLabel && (
        <TextLabel
          ref={widthLabelRef}
          x={artworkDimensions.x + artworkDimensions.width / 2}
          y={artworkDimensions.y + artworkDimensions.height + labelsOffset}
          tagFill={sizeLabelTagFill}
          textFill={sizeLabelTextFill}
        >
          {`${roundedWidth}"`}
        </TextLabel>
      )}
      {artworkImage && artworkDimensions && showHeightLabel && (
        <TextLabel
          ref={heightLabelRef}
          x={artworkDimensions.x - labelsOffset}
          y={artworkDimensions.y + artworkDimensions.height / 2}
          tagFill={sizeLabelTagFill}
          textFill={sizeLabelTextFill}
        >
          {`${roundedHeight}"`}
        </TextLabel>
      )}
      {artworkImage && artworkDimensions && showRemoveButton && onRemove && (
        <RemoveIcon
          x={artworkDimensions.x + artworkDimensions.width + BOX_SIZE / 2 / zoom}
          y={artworkDimensions.y - BOX_SIZE / 2 / zoom}
          onRemove={onRemove}
        />
      )}
    </Group>
  );
}
